import { StackLayout } from '@euler/app/components/StackLayout';
import { Typography } from '@euler/app/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  ComponentType,
  memo,
  ReactElement,
  useCallback,
  useState,
} from 'react';
import {
  FlatList,
  LayoutChangeEvent,
  ListRenderItem,
  Platform,
  SectionList,
  SectionListData,
  StyleProp,
  TextInput,
  TextInputProps,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { dropdown } from './icons';
import { DropdownCreatableProps } from './types';

export type DropdownMenuItemProps = {
  groupLabel?: string;
};

type SectionProps = {
  title: string;
};

type Props<T> = {
  keyExtractor: (item: T) => string;
  renderItem: ListRenderItem<T>;
  style?: StyleProp<ViewStyle>;
  creatable?: DropdownCreatableProps;
  contentContainerStyle?: StyleProp<ViewStyle>;
  searchInput?: TextInputProps;
  stickySearchInput?: boolean;
  ItemSeparatorComponent?: ComponentType<any>;
  onLayout?: (e: LayoutChangeEvent) => void;
} & (
  | {
      type: 'list';
      data: T[];
    }
  | {
      type: 'section-list';
      data: SectionListData<T, SectionProps>[];
      renderSectionHeader: (
        section: SectionListData<T, SectionProps>,
      ) => ReactElement | null;
    }
);

function DropdownMenuUnmemo<T>({
  keyExtractor,
  renderItem,
  creatable,
  searchInput,
  stickySearchInput,
  style,
  contentContainerStyle,
  ItemSeparatorComponent,
  onLayout,
  ...props
}: Props<T>) {
  const _renderSectionHeader =
    props.type === 'section-list' ? props.renderSectionHeader : undefined;

  const renderSectionHeader = useCallback(
    ({ section }: { section: SectionListData<T, SectionProps> }) => {
      if (_renderSectionHeader) {
        return _renderSectionHeader(section);
      }
      return <Typography.Text>{section.title}</Typography.Text>;
    },
    [_renderSectionHeader],
  );

  if (props.type === 'list') {
    return (
      <FlatList
        nativeID="dropdown-list"
        data={props.data}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        style={style}
        contentContainerStyle={contentContainerStyle}
        onLayout={onLayout}
        ItemSeparatorComponent={ItemSeparatorComponent}
        stickyHeaderIndices={
          (stickySearchInput && searchInput != null) ||
          (creatable != null && creatable.sticky)
            ? [0]
            : undefined
        }
        ListHeaderComponent={
          searchInput != null || creatable != null ? (
            <StackLayout>
              {creatable != null && <Creatable {...creatable} />}
              {searchInput != null && (
                <View>
                  <TextInput
                    placeholderTextColor="#758391"
                    placeholder="Search... "
                    clearButtonMode="always"
                    {...searchInput}
                  />
                  {searchInput.value?.length && Platform.OS === 'web' ? (
                    <SearchInputClear onChangeText={searchInput.onChangeText} />
                  ) : null}
                </View>
              )}
            </StackLayout>
          ) : undefined
        }
      />
    );
  } else {
    return (
      <SectionList
        nativeID="dropdown-list"
        sections={props.data}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        style={style}
        contentContainerStyle={contentContainerStyle}
        renderSectionHeader={renderSectionHeader}
        onLayout={onLayout}
        stickyHeaderIndices={
          stickySearchInput && searchInput != null ? [0] : undefined
        }
        ListHeaderComponent={
          searchInput != null ? (
            <View>
              <TextInput
                placeholderTextColor="#758391"
                placeholder="Search... "
                clearButtonMode="always"
                {...searchInput}
              />
              {searchInput.value?.length && Platform.OS === 'web' ? (
                <SearchInputClear onChangeText={searchInput.onChangeText} />
              ) : null}
            </View>
          ) : undefined
        }
      />
    );
  }
}

const SearchInputClear = memo(
  ({ onChangeText }: { onChangeText?: (text: string) => void }) => {
    const onClear = useCallback(() => {
      onChangeText?.('');
    }, [onChangeText]);

    return (
      <View
        css={`
          position: absolute;
          right: 10;
          top: 0;
          bottom: 0;
          padding: 0 10px;
          justify-content: center;
          align-items: center;
        `}
      >
        <TouchableOpacity
          onPress={onClear}
          css={`
            width: 16px;
            height: 16px;
            justify-content: center;
            align-items: center;
            border-radius: 16px;
            background-color: #ddd;
          `}
        >
          <dropdown.ClearIcon fill={'#555'} size={10} />
        </TouchableOpacity>
      </View>
    );
  },
);

const DropdownMenu = memo(DropdownMenuUnmemo) as typeof DropdownMenuUnmemo;

const Creatable = memo(
  ({
    title,
    input,
    inline,
    autoFocus,
    placeholder,
    placeholderTextColor,
    style,
    inputStyle,
    titleStyle,
    onCreate,
    onPress,
  }: DropdownCreatableProps) => {
    const { rpx } = useResponsiveContext();
    const [text, setText] = useState('');
    const [showInput, setShowInput] = useState(false);

    const handlePress = useCallback(() => {
      if (!inline) {
        onPress?.();
        return;
      }
      if (!input) {
        setShowInput(true);
      } else {
        onCreate?.(input);
      }
    }, [inline, onCreate, onPress, input]);

    const titleText = typeof title === 'function' ? title(input) : title;

    const onDoneEdit = useCallback(() => {
      if (text.trim()) {
        onCreate?.(text);
        setText('');
        setShowInput(false);
      }
    }, [onCreate, text]);

    return (
      <TouchableOpacity onPress={handlePress} style={style}>
        {showInput ? (
          <TextInput
            placeholder={placeholder}
            placeholderTextColor={placeholderTextColor}
            value={text}
            autoFocus={autoFocus}
            onChangeText={setText}
            onSubmitEditing={onDoneEdit}
            onBlur={() => {
              // a common issue on iOS Safari where the content doesn't
              // automatically reposition after keyboard dismissal
              Platform.OS === 'web' && window.scrollTo(0, 0);
            }}
            style={[
              {
                borderWidth: 1,
                borderColor: '#B5BDC4',
                padding: rpx(10),
                fontSize: rpx(16),
                borderRadius: rpx(5),
              },
              inputStyle,
            ]}
          />
        ) : (
          <Typography.Text
            size={16}
            family="bold"
            color="#7B4BF6"
            style={titleStyle}
          >
            {titleText ?? 'Create New'}
          </Typography.Text>
        )}
      </TouchableOpacity>
    );
  },
);

export default DropdownMenu;
